import React, { useState } from 'react';
import { useNavigate } from "react-router-dom";
import FlipClockTimer from "./FlipClockTimer";
import SoundCard from "./SoundCard";
import VolumeSlider from "./VolumeSlider";
import FavoritesPanel from "./FavoritesPanel";
import SettingsPage from "./SettingsPage";
import DailyRecommendation from "./DailyRecommendation";
import VideoComponent from "./VideoComponent";
import { Helmet } from "react-helmet";
import { beatPresets, noisePresets } from "../data/presets";
import { useAppContext } from "../contexts/AppContext";
import { useAudioPlayer } from "../hooks/useAudioPlayer";
import { useVolumeControl, usePanControl } from "../hooks/useVolumeControl";
import { useYouTubeAPI } from "../hooks/useYouTubeAPI";
import { AUDIO_CONFIG } from "../config/constants";
import "../style.css";


export default function BinauralBeatsPlayer() {
  const navigate = useNavigate();
  const [showSettings, setShowSettings] = useState(false);
  
  // Get state and functions from Context
  const {
    binauralAudioState,
    setBinauralAudioState,
    noiseAudioState,
    setNoiseAudioState,
    videoState,
    setVideoState,
    favorites,
    darkMode,
    ytApiReady,
    setYtApiReady,
    carrierFrequency,
    setCarrierFrequency,
    addToFavorites,
    removeFromFavorites,
    loadFavorite,
    toggleDarkMode,
    stopAll
  } = useAppContext();

  // Initialize YouTube API
  useYouTubeAPI(setYtApiReady);

  // Custom hooks for audio control
  const binauralPlayer = useAudioPlayer(binauralAudioState, setBinauralAudioState, carrierFrequency);
  const noisePlayer = useAudioPlayer(noiseAudioState, setNoiseAudioState);

  // Volume and pan control hooks
  useVolumeControl(binauralAudioState.gainNode, binauralAudioState.volume);
  useVolumeControl(noiseAudioState.gainNode, noiseAudioState.volume);
  usePanControl(binauralAudioState.panNodes, binauralAudioState.panWidth);

  const handleHelpClick = () => {
    stopAll();
    navigate("/help");
  };

  // Handle timer end
  const handleTimerEnd = () => {
    binauralPlayer.stopAudio();
    noisePlayer.stopAudio();
  };

  // Update volume handlers
  const handleBinauralVolumeChange = (value) => {
    setBinauralAudioState(prev => ({
      ...prev,
      volume: value
    }));
  };

  const handleNoiseVolumeChange = (value) => {
    setNoiseAudioState(prev => ({
      ...prev,
      volume: value
    }));
  };

  const handlePanWidthChange = (value) => {
    setBinauralAudioState(prev => ({
      ...prev,
      panWidth: value
    }));
  };

  const handleVideoVolumeChange = (value) => {
    setVideoState(prev => ({
      ...prev,
      volume: value
    }));
  };

  // Reset panning to full separation (a true binaural beat)
  const resetPanning = () => {
    setBinauralAudioState(prev => ({
      ...prev,
      panWidth: AUDIO_CONFIG.DEFAULT_PAN_WIDTH
    }));
  };

  // Wrapper for addToFavorites that creates the proper object
  const handleAddToFavorites = () => {
    if (videoState.id && !favorites.find(fav => fav.id === videoState.id)) {
      addToFavorites({
        id: videoState.id,
        url: videoState.url,
        title: videoState.title || `Video ${videoState.id}`
      });
    }
  };

  return (
    <>
      <section className="app-header" aria-label="Header">
        <h1 className="app-title">Binaural Beats</h1>
        <h2 className="app-subtitle">Relax, Focus and Sleep Better</h2>
      </section>

      <section className="app-container" aria-label="Binaural Beats Application">
        <Helmet>
          <title> Binaural Beats App | Relax & Focus with Binaural Beats free and online! </title>
          <meta name="description" content="Discover the power of binaural beats for relaxation, focus, and better sleep. Customize your experience with ambient noise and YouTube background music." />
          <meta property="og:description" content="Experience relaxation and focus with binaural beats and ambient noise." />
          <meta property="og:title" content="Binaural Beats App" />
          <meta property="og:image" content="/images/logo_bright.png" />
          <meta property="og:url" content="https://bb.yeyonetwork.com" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:description" content="Relax, focus, and sleep better with binaural beats and ambient noise. Try it now!" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <script type="application/ld+json">
            {`
              {
                "@context": "https://schema.org",
                "@type": "WebPage",
                "name": "Binaural Beats App",
                "description": "Experience relaxation and focus with binaural beats, ambient noise, and background music.",
                "url": "https://bb.yeyonetwork.com",
                "publisher": {
                  "@type": "Organization",
                  "name": "Binaural Beats App",
                  "logo": {
                    "@type": "ImageObject",
                    "url": "https://bb.yeyonetwork.com/images/logo_bright.png"
                  }
                }
              }
            `}
          </script>
        </Helmet>
        <section className="main-panel" aria-labelledby="main-panel-title">
          <div className="menu" aria-label="Main Menu">
            <h3 id="main-panel-title" className="section-title">Binaural Beat</h3>
      
            <div className="beats-gallery" role="list" aria-label="Binaural Beats Presets">
              {beatPresets.map(preset => (
                <SoundCard
                  key={preset.id}
                  preset={preset}
                  isActive={binauralAudioState.activePresetId === preset.id}
                  onActivate={binauralPlayer.handleBeatActivate}
                  cardType="beat"
                />
              ))}
            </div>
            
            <h3 className="section-title">Ambient Noise</h3>
            <div className="noise-gallery" role="list" aria-label="Ambient Noise Presets">
              {noisePresets.map(noisePreset => (
                <SoundCard
                  key={noisePreset.id}
                  preset={noisePreset}
                  isActive={noiseAudioState.activePresetId === noisePreset.id}
                  onActivate={noisePlayer.handleNoiseActivate}
                  cardType="noise"
                />
              ))}
            </div>
            
            {/* Video Component */}
            <VideoComponent
              videoState={videoState}
              setVideoState={setVideoState}
              favorites={favorites}
              addToFavorites={handleAddToFavorites}
              ytApiReady={ytApiReady}
            />

            {/* Volume and control sliders */}
            <div className="sliders" aria-label="Volume and Stereo Controls">
              <VolumeSlider
                label="Binaural Beats Volume"
                value={binauralAudioState.volume}
                onChange={handleBinauralVolumeChange}
              />

              <VolumeSlider
                label="Noise Volume"
                value={noiseAudioState.volume}
                onChange={handleNoiseVolumeChange}
              />

              <VolumeSlider
                label="Video Volume"
                value={videoState.volume}
                onChange={handleVideoVolumeChange}
              />

              <VolumeSlider
                label="Stereo Width"
                value={binauralAudioState.panWidth}
                onChange={handlePanWidthChange}
                hasResetButton={true}
                onReset={resetPanning}
                valueFormatter={(val) => (val / 100).toFixed(2)}
              />
            </div>
            
            <button 
              onClick={stopAll} 
              className="button red"
              aria-label="Stop All Audio"
            >
              Stop All
            </button>
          </div>
        </section>
        
        <section className="side-panel" aria-labelledby="side-panel-title">
          <FlipClockTimer 
            onTimerEnd={handleTimerEnd} 
            aria-label="Session Timer"
          />

          <DailyRecommendation 
            setVideoState={setVideoState} 
            aria-label="Daily Recommendation"
          />

          <FavoritesPanel 
            favorites={favorites}
            loadFavorite={loadFavorite}
            removeFromFavorites={removeFromFavorites}
            aria-label="Favorites Panel"
          />
          
          <section className="app-logo-section" aria-label="App Logo">
            <img
              src={darkMode ? "/images/logo_dark.png" : "/images/logo_bright.png"}
              alt="App Logo"
              className="app-logo"
            />
          </section>

          <section className="donate" aria-label="Support the App">
            <a 
              href="https://ko-fi.com/V7V2IBLBD" 
              target="_blank" 
              rel="noopener noreferrer"
              aria-label="Buy Me a Coffee on Ko-fi"
            >
              <img
                height="36"
                style={{ border: "0px", height: "36px" }}
                src="https://storage.ko-fi.com/cdn/kofi6.png?v=6"
                alt="Buy Me a Coffee at ko-fi.com"
              />
            </a>
          </section>
          
          <section className="menu-bar" aria-label="Menu Bar">
            <button 
              onClick={toggleDarkMode} 
              className="menu-button" 
              title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
              aria-label={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
            >
              {darkMode ? "☀️" : "🌙"}
            </button>
            
            <button 
              className="menu-button" 
              title="Help" 
              onClick={handleHelpClick}
              aria-label="Help"
            >
              ?
            </button>
            
            <button
              className="menu-button"
              title="Settings"
              onClick={() => setShowSettings(true)}
              aria-label="Settings"
            >
              ⚙️
            </button>

            {showSettings && (
              <SettingsPage
                carrierFrequency={carrierFrequency}
                setCarrierFrequency={setCarrierFrequency}
                onClose={() => setShowSettings(false)}
                aria-label="Settings Page"
              />
            )}
          </section>
          
        </section>
      </section>
      <section className="footer" aria-label="Footer">
        <footer className="page-footer">
          By <a 
            href="https://github.com/Sergiodiaz53" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            Sergiodiaz53
          </a> • ℗ © • 2025. Enjoy!
          <div className="social-icons">
            <a 
              href="https://twitter.com/intent/tweet?text=Check%20this%20tool%20to%20generate%20Binaural%20Beats%20and%20Ambient%20noise%20along%20with%20any%20YouTube%20music%20video!&url=https://bb.yeyonetwork.com" 
              target="_blank" 
              rel="noopener noreferrer" 
              aria-label="Share on Twitter"
            >
              <img src="/icons/twitter.svg" alt="Twitter" className="social-icon" />
            </a>
            <a 
              href="https://www.facebook.com/sharer/sharer.php?u=https://bb.yeyonetwork.com&quote=Check%20this%20tool%20to%20generate%20Binaural%20Beats%20and%20Ambient%20noise%20along%20with%20any%20YouTube%20music%20video!" 
              target="_blank" 
              rel="noopener noreferrer" 
              aria-label="Share on Facebook"
            >
              <img src="/icons/facebook.svg" alt="Facebook" className="social-icon" />
            </a>
            <a 
              href="https://www.linkedin.com/shareArticle?mini=true&url=https://bb.yeyonetwork.com&title=Binaural%20Beats%20App&summary=Check%20this%20tool%20to%20generate%20Binaural%20Beats%20and%20Ambient%20noise%20along%20with%20any%20YouTube%20music%20video!" 
              target="_blank" 
              rel="noopener noreferrer" 
              aria-label="Share on LinkedIn"
            >
              <img src="/icons/linkedin.svg" alt="LinkedIn" className="social-icon" />
            </a>
          </div>
        </footer>
      </section>
    </>
  );
}